import { useEffect, useMemo } from 'react';
import useCurrentUser from '@/hooks/useCurrentUser';
import useInstitution from '@/hooks/useInstitution';
import { useStudentVerificationStatus } from '@/hooks/useStudentVerificationStatus';
import { useStudentResendVerification } from '@/hooks/useStudentResendVerification';
import useAlert from '@/components/Alert/hooks';
import Analytics from '@/system/Analytics';
import { OnboardingEvents } from '@/system/Analytics/events/OnboardingEvents';
import InstitutionLogo from '@/common/InstitutionLogo';
import { Institution } from '@/lib/Institutions/types';
import Text from '@/components/Text';
import { EducationLevel, EducationLevelToText } from '@/lib/Account/types';
import Heading from '@/common/Heading';
import Button from '@/components/Button';
import Loader from '@/components/Loader';
import ListItem from '@/common/ListItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';
import { StudentVerificationStatus, StudentVerificationStatusToText } from '@/lib/Student/types';
import Badge from '@/common/Badge';
import { twMerge } from 'tailwind-merge';

function StudentProfile({
  user,
  onStudentProfileRefetch,
}: {
  user: NonNullable<ReturnType<typeof useCurrentUser>['data']>;
  onStudentProfileRefetch: () => void;
}) {
  const { data: institution } = useInstitution(user?.account?.studentStatus?.institutions?.[0]);
  const student = useMemo(() => user?.account?.studentStatus, [user]);
  const isPending = useMemo(() => student?.verified === 'pending', [student]);
  const {
    refetch,
    data: verification,
    isLoading: isLoadingVerificationStatus,
    error: verificationError,
    isError: isErrorVerificationStatus,
  } = useStudentVerificationStatus(user?.account?.id as string);
  const isExpired = useMemo(() => new Date(verification?.expiresAt ?? 0) < new Date() && (verification?.status === 'pending' || verification?.status === 'expired'), [verification]);

  useEffect(() => {
    if (isPending) {
      refetch();
      onStudentProfileRefetch();
    }
  }, []);

  const alert = useAlert();
  const resendVerificationMutation = useStudentResendVerification(user?.account?.id as string);
  const handleResendVerification = async () => {
    try {
      await resendVerificationMutation.mutate({ token: verification?.token as string });
      alert.show({
        title: 'Verification email sent',
        message: 'Please check your email for the verification link',
        confirmText: 'OK',
      });
      Analytics.track(OnboardingEvents.RESEND_VERIFICATION_EMAIL, {
        userId: user?.account?.id as string,
      });
    } catch (error) {
      alert.show({
        type: 'error',
        title: 'Failed to send verification email',
        message: (error as Error).message,
        confirmText: 'OK',
      });
      Analytics.track(OnboardingEvents.RESEND_VERIFICATION_EMAIL_ERROR, {
        userId: user?.account?.id as string,
      });
    }
  };

  if (!user?.account?.studentStatus) return null;

  return (
    <div className={clsx('flex flex-col gap-4')}>
      <ListItem>
        <div className="flex items-center space-x-4 w-full">
          <div className="text-icon-primary">
            <FontAwesomeIcon icon={faGraduationCap as IconProp} className="size-6 text-secondary" />
          </div>

          <div className="flex flex-row justify-between w-full">
            <div className="w-3/4 flex space-x-3 items-center">
              <h4 className="truncate">
                <Text>Student profile</Text>
              </h4>
              <Badge
                variant='default'
                className={twMerge('uppercase h-6', user?.account?.studentStatus?.verified === 'verified'
                  ? 'text-success bg-success/10'
                  : user?.account?.studentStatus?.verified === 'pending'
                  ? 'text-warning bg-warning/10'
                  : 'text-error bg-error/10')}
              >
                <span className="text-[12px]">
                  {
                    StudentVerificationStatusToText[
                      user?.account?.studentStatus?.verified as StudentVerificationStatus
                    ]
                  }
                </span>
              </Badge>
            </div>
          </div>
        </div>
      </ListItem>
      <div className="p-6 pt-0 space-y-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="order-1 flex-none">
            <InstitutionLogo height={96} width={96} institution={institution as Institution} />
          </div>
          <div className="order-2 flex flex-col gap-2 flex-1">
            <div className="flex gap-4">
              <Text size="sm">
                <span className="text-secondary">Email:</span>
              </Text>
              <Text size="sm" className="col-span-2">
                {user?.account?.studentStatus?.email}
              </Text>
            </div>
            <div className="flex gap-4">
              <Text size="sm">
                <span className="text-secondary">Institution:</span>
              </Text>
              <Text size="sm" className="col-span-2">
                {institution?.name}, {institution?.country}
              </Text>
            </div>
            <div className="flex gap-4">
              <Text size="sm">
                <span className="text-secondary">Education level:</span>
              </Text>
              <Text size="sm" className="col-span-2">
                {user?.account?.studentStatus?.educationLevel
                  ? EducationLevelToText[
                      (
                        user?.account?.studentStatus?.educationLevel as EducationLevel
                      ).toUpperCase() as keyof typeof EducationLevelToText
                    ]
                  : 'N/A'}
              </Text>
            </div>
          </div>
        </div>
        <div className="flex gap-4">
          {isLoadingVerificationStatus ? (
            <Loader />
          ) : (
            <>
              {isExpired && (
                <div className="bg-warning/10 text-warning p-2 rounded-xl w-full flex flex-col gap-2 items-center place-items-center">
                  <Heading as="h4" className="font-bold">
                    Verification expired
                  </Heading>
                  <Text size="sm" className="text-center">Send a new verification email for {user?.account?.studentStatus?.email}</Text>
                  <Button
                    onClick={handleResendVerification}
                    type="Link"
                    size="md"
                    className="text-inherit underline md:h-0"
                  >
                    Click here to send a new verification email
                  </Button>
                </div>
              )}
              {isErrorVerificationStatus && (
                <div className="bg-error/10 text-error p-2 rounded-xl w-full flex flex-col items-center justify-center">
                  <Heading as="h4" textCentered className="font-bold">
                    Error
                  </Heading>
                  <Text size="sm">{verificationError as string}</Text>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentProfile;
